import React, { useCallback, useState } from 'react';
import {
  Icon,
  IconName,
  Text,
} from '@usealloy/component-library';
import {
  COLUMN_SPACING,
  NODE_HEIGHT,
  NODE_HEIGHT_XL,
  NODE_WIDTH,
  ROW_SPACING,
  EXPANDED_SNIPPET_NODE_GUTTER,
} from 'flow/service/graph';
import type { SnippetNode as ISnippetNode, WorkflowNodeProps } from 'flow/service/graph';
import BaseNode from '../BaseNode';
import {
  StyledChildBackground,
  StyledDropNode,
  StyledLabelContainer,
  StyledLabelText,
  StyledSnippetFocusable,
  StyledSnippetIconContainer,
  StyledVersionBadge,
  collapsedSnippetStyle,
  expandedSnippetStyle,
  snippetFocusedStyle,
} from './styles';
import {
  selectActionByPublicId,
  selectNodeState,
  selectSnippetCollapsedState,
  selectSubgraphDimensionsById,
} from 'flow/store/redux/selectors';
import { defaultNodeDimensions } from 'flow/service/graph/layout/nodeMatrix';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { toggleSnippetCollapsibleThunk } from 'flow/store/redux/thunks';

function SnippetNode(props: WorkflowNodeProps<ISnippetNode>) {
  const { id, type } = props;
  const { isCollapsed } = useAppSelector(state => selectSnippetCollapsedState(id)(state));
  const nodeState = useAppSelector(state => selectNodeState(state, id));
  const snippetDimensions = useAppSelector(state => selectSubgraphDimensionsById(id)(state));
  const snippetAction = useAppSelector(selectActionByPublicId(id));

  const { nodeHeight, nodeWidth } = snippetDimensions ?? defaultNodeDimensions;
  const dispatch = useAppDispatch();

  const version = snippetAction?.snippet?.publicVersion;

  // Todo:
  // - dynamically load in snippet configurations

  const [isIconHovered, setIsIconHovered] = useState(false);

  const handleIconClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(toggleSnippetCollapsibleThunk(id));
  }, [dispatch, id]);

  const SnippetIcon = (
    <StyledSnippetIconContainer
      onMouseEnter={() => setIsIconHovered(true)}
      onMouseLeave={() => setIsIconHovered(false)}
      onClick={handleIconClick}
    >
      <Icon
        icon={isIconHovered ? IconName.UpRightDownLeftFromCenter : IconName.AddressCard}
        size="large"
        color="var(--cl-gray-000)"
      />
    </StyledSnippetIconContainer>
  );

  const labelText = isCollapsed && version != null
    ? `Linked Snippet v${version}`
    : 'Linked Snippet';

  const Label = (
    <StyledLabelContainer>
      <Text size={1} style={{ color: 'var(--cl-text-muted)' }}>
        {labelText}
      </Text>
      <StyledLabelText>
        Snippet Name
      </StyledLabelText>
    </StyledLabelContainer>
  );

  const VersionBadge = (
    <StyledVersionBadge>
      v{version}
    </StyledVersionBadge>
  );

  const expandedWidth = (
    (NODE_WIDTH * nodeWidth)
    + (COLUMN_SPACING * (nodeWidth - 1))
  );

  const width = isCollapsed ? NODE_WIDTH : expandedWidth;

  const expandedBackgroundHeight = (
    (NODE_HEIGHT * (nodeHeight - 1))
    + (ROW_SPACING * (nodeHeight - 2))
    + EXPANDED_SNIPPET_NODE_GUTTER
  );

  return (
    <>
      <StyledSnippetFocusable nodeState={nodeState}>
        <BaseNode
          id={id}
          icon={SnippetIcon}
          label={Label}
          style={{
            ...(isCollapsed ? collapsedSnippetStyle : expandedSnippetStyle),
            borderStyle: 'solid',
            borderColor: 'var(--cl-gray-300)',
            padding: 'var(--cl-space-3)',
            width,
            height: NODE_HEIGHT_XL,
          }}
          stateStyle={{
            baseBackgroundColor: 'var(--cl-gray-000)',
            highlighted: {
              backgroundColor: 'var(--cl-gray-000)',
            },
            hovered: {
              backgroundColor: 'var(--cl-gray-100)',
            },
            focused: snippetFocusedStyle,
            'focused-hovered': snippetFocusedStyle,
          }}
          type={type}
          disable={{ nodeMenu: isCollapsed, nodeMenuHoverState: !isCollapsed }}
        >
          {!isCollapsed ? VersionBadge : undefined}
        </BaseNode>
        {
          !isCollapsed && (
            <StyledChildBackground
              nodeState={nodeState}
              height={expandedBackgroundHeight}
              width={width}
            />
          )
        }
      </StyledSnippetFocusable>
      {
        isCollapsed && <StyledDropNode nodeState={nodeState} />
      }
    </>
  );
}

export default React.memo(SnippetNode);
